import * as React from 'react';
import classnames from 'classnames';

import PageSection from 'components/page/page-section/PageSection';
import RichTextEditor from 'components/inputs/rich-text-editor/RichTextEditor';

import { expandDataProps } from 'utils/helper/common';
import { sanitizeGenericHtml } from 'utils/sanitize';

import './information-card.scss';

import type { Data } from 'types';

type Props = {
  title: string,
  description: React.ReactNode,
  brief?: string,
  isPlainText?: boolean,
  required?: boolean,
  hasError?: boolean,
  hasChanges?: boolean,
  action?: React.ReactNode,
  previewMode: 'external' | 'inline',
  customContentClass?: string,
  data?: Data
};

class InformationCard extends React.PureComponent<Props> {
  static defaultProps = {
    previewMode: 'inline'
  };

  renderDescription() {
    const { description, isPlainText } = this.props;

    if (!description) {
      return (
        <span>--</span>
      );
    }

    if (isPlainText) {
      return (
        <div className="text-overflow-wrap text-preserve-whitespaces">
          {description}
        </div>
      );
    }

    if (typeof description === 'object') {
      return description;
    }

    const extraEditorProps: {
      key?: string
    } = {};
    if (typeof description === 'string') {
      extraEditorProps.key = description;
    }

    return (
      <RichTextEditor
        {...extraEditorProps}
        canEdit={false}
        value={description}
      />
    );
  }

  render() {
    const { title, description, brief, required, action, hasError, hasChanges, previewMode, customContentClass, data } = this.props;

    const classes = classnames({
      'information-card': true,
      'information-card--error': hasError
    });

    const contentClasses = classnames('information-card__content', {
      [String(customContentClass)]: customContentClass
    });

    const titleClasses = classnames({
      'information-card__title': true,
      'information-card__title--changed': hasChanges,
      'text-overflow-ellipsis': true
    });

    const actionClasses = classnames({
      'information-card__action': true,
      'information-card__action--error': hasError
    });

    return (
      <div
        className={classes}
        {...expandDataProps(data)}
      >
        <PageSection
          style="volume"
          padding="none"
          margin="none"
          border="none"
        >
          <div className={contentClasses}>
            <div className="information-card__information">
              <div className={titleClasses}>
                <span>{title}</span>
                {
                  required && (
                    <span className="information-card__required">&nbsp;*</span>
                  )
                }
              </div>
              {
                brief && description && <span
                  className="information-card__brief"
                  dangerouslySetInnerHTML={{ __html: sanitizeGenericHtml(brief) }}
                />
              }
              {
                previewMode === 'inline' &&
                <div className="information-card__description">
                  {this.renderDescription()}
                </div>
              }
            </div>
            {Boolean(action) && (
              <div className={actionClasses}>
                {action}
              </div>
            )}
          </div>
        </PageSection>
      </div>
    );
  }
}

export default InformationCard;
